import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  CheckOutlined,
  HighlightOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PartitionOutlined,
  PicLeftOutlined,
  PictureFilled,
  RedoOutlined,
  StrikethroughOutlined,
  TableOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { UploadedFile } from '@frontend/data-access';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
} from '@lexical/rich-text';
import { $isAtNodeEnd, $wrapNodes } from '@lexical/selection';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { Button, Dropdown, Form, Input, Modal } from 'antd';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  BaseSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import ImageUpload from '../../image-upload/image-upload';
import { INSERT_EXCALIDRAW_COMMAND } from './ExcalidrawPlugin';
import { INSERT_IMAGE_COMMAND } from './ImagePlugin';
import { INSERT_TABLE_COMMAND } from './TablePlugin';
import { INSERT_YOUTUBE_COMMAND } from './YouTubePlugin';

const LowPriority = 1;

const supportedBlockTypes = new Set([
  'paragraph',
  'quote',
  'code',
  'h1',
  'h2',
  'ul',
  'ol',
]);

const blockTypeToBlockName = {
  code: 'Code Block',
  h1: 'Large Heading',
  h2: 'Small Heading',
  h3: 'Heading',
  h4: 'Heading',
  h5: 'Heading',
  ol: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
  ul: 'Bulleted List',
};

function Divider() {
  return <div className="divider" />;
}

function positionEditorElement(editor: any, rect: any) {
  if (rect === null) {
    editor.style.opacity = '0';
    editor.style.top = '-1000px';
    editor.style.left = '-1000px';
  } else {
    editor.style.opacity = '1';
    editor.style.top = `${rect.top + rect.height + window.pageYOffset + 10}px`;
    editor.style.left = `${
      rect.left + window.pageXOffset - editor.offsetWidth / 2 + rect.width / 2
    }px`;
  }
}

function FloatingLinkEditor({ editor }: { editor: any }) {
  const editorRef = useRef(null);
  const inputRef = useRef(null);
  const mouseDownRef = useRef(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [isEditMode, setEditMode] = useState(true);
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(
    null
  );

  const updateLinkEditor = useCallback(() => {
    const selection: BaseSelection | null = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('');
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection: Selection | null = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      nativeSelection &&
      selection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner = rootElement;
        while (inner.firstElementChild != null) {
          inner = inner.firstElementChild;
        }
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }

      if (!mouseDownRef.current) {
        positionEditorElement(editorElem, rect);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      positionEditorElement(editorElem, null);
      setLastSelection(null);
      setEditMode(false);
      setLinkUrl('');
    }

    return true;
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }: { editorState: any }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        LowPriority
      )
    );
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);

  // useEffect(() => {
  //   if (isEditMode && inputRef.current) {
  //     inputRef.current.focus();
  //   }
  // }, [isEditMode]);

  const handleLinkSave = () => {
    if (lastSelection !== null) {
      if (linkUrl !== '') {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
      }
      setEditMode(false);
    }
  };

  return (
    <div ref={editorRef} className="link-editor">
      {isEditMode ? (
        <div className="link-input !flex gap-1">
          <input
            ref={inputRef}
            value={linkUrl}
            className="bg-transparent w-full"
            onChange={(event) => {
              setLinkUrl(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleLinkSave();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                setEditMode(false);
              }
            }}
          />
          <span className="cursor-pointer" onClick={handleLinkSave}>
            <CheckOutlined />
          </span>
        </div>
      ) : (
        <div className="link-input">
          <a href={linkUrl} target="_blank" rel="noopener noreferrer">
            {linkUrl}
          </a>
          <div
            className="link-edit"
            role="button"
            tabIndex={0}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setEditMode(true);
            }}
          />
        </div>
      )}
    </div>
  );
}

function getSelectedNode(selection: any) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

export default function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('common');
  const toolbarRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [blockType, setBlockType] =
    useState<keyof typeof blockTypeToBlockName>('paragraph');
  const [isRTL, setIsRTL] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);

  const formatParagraph = () => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode());
        }
      });
    }
  };

  const formatLargeHeading = () => {
    if (blockType !== 'h1') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode('h1'));
        }
      });
    }
  };

  const formatSmallHeading = () => {
    if (blockType !== 'h2') {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode('h2'));
        }
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode());
        }
      });
    }
  };

  const items = [
    {
      key: 'paragraph',
      icon: <PicLeftOutlined />,
      label: <div onClick={formatParagraph}>Normal</div>,
    },
    {
      key: 'h1',
      icon: <span className="text-md ">H1</span>,
      label: <div onClick={formatLargeHeading}>Large Heading</div>,
    },
    {
      key: 'h2',
      icon: <span className="text-md ">H2</span>,
      label: <div onClick={formatSmallHeading}>Small Heading</div>,
    },
    {
      key: 'ul',
      icon: <UnorderedListOutlined />,
      label: <div onClick={formatBulletList}>Bullet List</div>,
    },
    {
      key: 'ol',
      icon: <OrderedListOutlined />,
      label: <div onClick={formatNumberedList}>Numbered List</div>,
    },
    {
      key: 'quote',
      icon: <HighlightOutlined />,
      label: <div onClick={formatQuote}>Quote</div>,
    },
  ];

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type as keyof typeof blockTypeToBlockName);
        }
      }
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));

      // Check RTL - get the anchor node's parent element direction
      try {
        const anchorNode = selection.anchor.getNode();
        const element = anchorNode.getTopLevelElement();
        if (element) {
          setIsRTL(element.getDirection() === 'rtl');
        }
      } catch (e) {
        // If we can't determine RTL, default to false
        setIsRTL(false);
      }

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const onFinish = ({ link }: { link: string }) => {
    const match =
      /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/.exec(
        link
      );
    const id = match ? (match?.[2].length === 11 ? match[2] : null) : null;

    id && editor.dispatchCommand(INSERT_YOUTUBE_COMMAND, id);
  };

  const YoutubeLink = () => {
    return (
      <Form name="control-hooks" onFinish={onFinish} style={{ maxWidth: 600 }}>
        <div className="site-input-group-wrapper">
          <Input.Group compact>
            <Form.Item name="link" rules={[{ required: true }]}>
              <Input
                style={{ width: 250 }}
                placeholder="https://youtu.be/gQGDxHv0Uwo"
              />
            </Form.Item>
            <Button htmlType="submit" size="large" type="primary">
              open
            </Button>
          </Input.Group>
        </div>
      </Form>
    );
  };

  const [openImageUploadModal, setOpenImageUploadModal] =
    useState<boolean>(false);
  const [openTableModal, setOpenTableModal] = useState<boolean>(false);

  const handleUploadModal = () => {
    setOpenImageUploadModal(!openImageUploadModal);
  };

  const handleTableModal = () => {
    setOpenTableModal(!openTableModal);
  };

  const onImageUploadSuccess = (params: UploadedFile) => {
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
      altText: '',
      src: params.url || '',
    });
    handleUploadModal();
  };

  const onTableInsert = (values: { rows: string; columns: string }) => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      rows: values.rows,
      columns: values.columns,
      includeHeaders: false,
    });
    handleTableModal();
  };

  return (
    <div className="toolbar" ref={toolbarRef}>
      <button
        disabled={!canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        className="toolbar-item spaced"
        aria-label="Undo"
        type="button"
      >
        <UndoOutlined />
      </button>
      <button
        disabled={!canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        className="toolbar-item"
        aria-label="Redo"
        type="button"
      >
        <RedoOutlined />
      </button>
      <Divider />
      {supportedBlockTypes.has(blockType) && (
        <>
          <Dropdown
            menu={{
              items,
            }}
            arrow
            trigger={['click']}
            placement="bottom"
          >
            <button
              className="toolbar-item block-controls"
              aria-label="Formatting Options"
              type="button"
            >
              {items.find((item) => item.key === blockType)?.icon}
              <span className="text">
                {blockTypeToBlockName[blockType]}
              </span>{' '}
              <i className="chevron-down" />
            </button>
          </Dropdown>
          <Divider />
        </>
      )}
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
        aria-label="Format Bold"
        type="button"
      >
        <BoldOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
        aria-label="Format Italics"
        type="button"
      >
        <ItalicOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
        }}
        className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
        aria-label="Format Underline"
        type="button"
      >
        <UnderlineOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
        }}
        className={'toolbar-item spaced ' + (isStrikethrough ? 'active' : '')}
        aria-label="Format Strikethrough"
        type="button"
      >
        <StrikethroughOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
        }}
        className={'toolbar-item spaced ' + (isCode ? 'active' : '')}
        aria-label="Insert Code"
        type="button"
      >
        &lt;/&gt;
      </button>
      <button
        onClick={insertLink}
        className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
        aria-label="Insert Link"
        type="button"
      >
        <LinkOutlined />
      </button>
      {isLink &&
        createPortal(<FloatingLinkEditor editor={editor} />, document.body)}
      <Divider />
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
        }}
        className="toolbar-item spaced"
        aria-label="Left Align"
        type="button"
      >
        <AlignLeftOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
        }}
        className="toolbar-item spaced"
        aria-label="Center Align"
        type="button"
      >
        <AlignCenterOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
        }}
        className="toolbar-item spaced"
        aria-label="Right Align"
        type="button"
      >
        <AlignRightOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
        }}
        className="toolbar-item"
        aria-label="Justify Align"
        type="button"
      >
        <i className="format justify-align" />
      </button>
      <Divider />
      <button
        onClick={() => {
          setOpenImageUploadModal(!openImageUploadModal);
        }}
        className="toolbar-item"
        aria-label="Image upload"
        type="button"
      >
        <PictureFilled />
      </button>
      <button
        onClick={() => {
          setOpenTableModal(!openTableModal);
        }}
        className="toolbar-item"
        aria-label="Insert table"
        type="button"
      >
        <TableOutlined />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(INSERT_EXCALIDRAW_COMMAND, undefined);
        }}
        className="toolbar-item"
        aria-label="Insert Excalidraw"
        type="button"
      >
        <PartitionOutlined />
      </button>
      {/* <Popover
        content={<YoutubeLink />}
        title="YouTube video url"
        trigger="click"
        type="button"
      >
        <button className="toolbar-item" aria-label="YouTube video">
          <i className="format youtube" />
        </button>
      </Popover> */}
      <Modal
        title={t('upload_image_modal_title')}
        open={openImageUploadModal}
        onOk={() => handleUploadModal()}
        onCancel={() => handleUploadModal()}
      >
        <ImageUpload
          title={t('upload_image_title')}
          description={t('upload_image_default_support_description')}
          compact={false}
          crop={{ quality: 1, aspect: 1200 / 600, minZoom: 1, maxZoom: 10 }}
          onUploadSuccess={onImageUploadSuccess}
        />
      </Modal>
      <Modal
        title={t('insert_table')}
        open={openTableModal}
        onCancel={() => handleTableModal()}
        footer={null}
      >
        <Form
          name="table-insert"
          onFinish={onTableInsert}
          initialValues={{ rows: '3', columns: '3' }}
          layout="vertical"
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="Columns"
              name="columns"
              rules={[
                { required: true, message: 'Please input number of columns' },
              ]}
            >
              <Input type="number" min={1} max={20} />
            </Form.Item>
            <Form.Item
              label="Rows"
              name="rows"
              rules={[
                { required: true, message: 'Please input number of rows' },
              ]}
            >
              <Input type="number" min={1} max={100} />
            </Form.Item>
          </div>
          <div className="flex justify-end">
            <Form.Item noStyle>
              <Button type="primary" htmlType="submit">
                {t('insert_table')}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
